/**
 * BlockScheduleModal Component
 * Modal for blocking a professional's schedule for specific dates
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Radio,
  RadioGroup,
  Chip,
} from '@mui/material';
import { Block, Warning } from '@mui/icons-material';
import {
  ProfessionalForSchedule,
  ClinicForSchedule,
} from '../../../types/professionalSchedule';
import { professionalScheduleService } from '../../../services/professionalScheduleService';

/**
 * Block reasons available for selection
 */
const BLOCK_REASONS = [
  { value: 'Falta', label: 'Falta' },
  { value: 'Férias', label: 'Férias' },
  { value: 'Congresso/Evento', label: 'Congresso/Evento' },
  { value: 'Licença médica', label: 'Licença médica' },
  { value: 'Outro', label: 'Outro' },
] as const;

type BlockType = 'single' | 'period';

interface BlockScheduleModalProps {
  open: boolean;
  onClose: () => void;
  onBlock: () => void;
  professional: ProfessionalForSchedule;
  clinic: ClinicForSchedule;
}

const BlockScheduleModal: React.FC<BlockScheduleModalProps> = ({
  open,
  onClose,
  onBlock,
  professional,
  clinic,
}) => {
  // Form state
  const [blockType, setBlockType] = useState<BlockType>('single');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [reason, setReason] = useState<string>('Falta');
  const [customReason, setCustomReason] = useState<string>('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [countLoading, setCountLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slotsCount, setSlotsCount] = useState<number | null>(null);

  // Set default date to today
  useEffect(() => {
    if (open) {
      const today = new Date().toISOString().split('T')[0];
      setStartDate(today);
      setEndDate(today);
      setBlockType('single');
      setReason('Falta');
      setCustomReason('');
      setError(null);
      setSlotsCount(null);
    }
  }, [open]);

  // Count slots when dates change
  useEffect(() => {
    const countSlots = async () => {
      if (!startDate || (blockType === 'period' && !endDate)) {
        setSlotsCount(null);
        return;
      }

      const effectiveEndDate = blockType === 'single' ? startDate : endDate;

      // Validate date range
      if (new Date(startDate) > new Date(effectiveEndDate)) {
        setSlotsCount(null);
        return;
      }

      setCountLoading(true);
      try {
        const count = await professionalScheduleService.countSlotsInPeriod({
          professionalId: professional.id,
          startDate,
          endDate: effectiveEndDate,
          clinicId: clinic.id,
        });
        setSlotsCount(count);
      } catch (err) {
        console.error('Error counting slots:', err);
        setSlotsCount(null);
      } finally {
        setCountLoading(false);
      }
    };

    const timeoutId = setTimeout(countSlots, 300);
    return () => clearTimeout(timeoutId);
  }, [startDate, endDate, blockType, professional.id, clinic.id]);

  const handleBlockTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBlockType(event.target.value as BlockType);
    if (event.target.value === 'single') {
      setEndDate(startDate);
    }
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    if (blockType === 'single') {
      setEndDate(value);
    }
  };

  const validateForm = (): boolean => {
    if (!startDate) {
      setError('Data é obrigatória');
      return false;
    }

    if (blockType === 'period' && !endDate) {
      setError('Data final é obrigatória para bloqueio de período');
      return false;
    }

    if (blockType === 'period' && new Date(startDate) > new Date(endDate)) {
      setError('Data inicial não pode ser maior que a data final');
      return false;
    }

    if (!reason) {
      setError('Motivo é obrigatório');
      return false;
    }

    if (reason === 'Outro' && !customReason.trim()) {
      setError('Especifique o motivo do bloqueio');
      return false;
    }

    return true;
  };

  const handleBlock = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const effectiveEndDate = blockType === 'single' ? startDate : endDate;
      const effectiveReason = reason === 'Outro' ? customReason : reason;

      const result = await professionalScheduleService.blockSlotsInPeriod({
        professionalId: professional.id,
        startDate,
        endDate: effectiveEndDate,
        reason: effectiveReason,
        clinicId: clinic.id,
      });

      if (result.affectedAppointments > 0) {
        setError(
          `Atenção: ${result.affectedAppointments} agendamento(s) existente(s) foram afetados. ` +
          `Os pacientes devem ser notificados.`
        );
      }

      onBlock();
      handleClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao bloquear agenda';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStartDate('');
    setEndDate('');
    setReason('Falta');
    setCustomReason('');
    setError(null);
    setSlotsCount(null);
    onClose();
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Block color="warning" />
          <Typography variant="h6">Bloquear Agenda</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Professional and clinic info */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Profissional: <strong>{professional.name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Clínica: <Chip label={clinic.tradeName} size="small" />
            </Typography>
          </Box>

          {/* Block type selection */}
          <FormControl component="fieldset">
            <Typography variant="subtitle2" gutterBottom>
              Tipo de bloqueio:
            </Typography>
            <RadioGroup
              row
              value={blockType}
              onChange={handleBlockTypeChange}
            >
              <FormControlLabel
                value="single"
                control={<Radio />}
                label="Dia único"
              />
              <FormControlLabel
                value="period"
                control={<Radio />}
                label="Período"
              />
            </RadioGroup>
          </FormControl>

          {/* Date selection */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label={blockType === 'single' ? 'Data' : 'De'}
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
              required
            />
            {blockType === 'period' && (
              <TextField
                label="Até"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
                required
                inputProps={{ min: startDate }}
              />
            )}
          </Box>

          {/* Reason selection */}
          <FormControl fullWidth required>
            <InputLabel>Motivo</InputLabel>
            <Select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              label="Motivo"
            >
              {BLOCK_REASONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Custom reason field */}
          {reason === 'Outro' && (
            <TextField
              label="Especifique o motivo"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              fullWidth
              required
              placeholder="Digite o motivo do bloqueio"
            />
          )}

          {/* Slots count preview */}
          {(slotsCount !== null || countLoading) && (
            <Alert
              severity={slotsCount === 0 ? 'warning' : 'info'}
              icon={countLoading ? <CircularProgress size={20} /> : <Warning />}
            >
              {countLoading ? (
                'Calculando slots...'
              ) : slotsCount === 0 ? (
                `Nenhum slot encontrado para o ${blockType === 'single' ? 'dia ' + formatDate(startDate) : 'período de ' + formatDate(startDate) + ' a ' + formatDate(endDate)}`
              ) : (
                `${slotsCount} slot${(slotsCount ?? 0) > 1 ? 's' : ''} será${(slotsCount ?? 0) > 1 ? 'ão' : ''} bloqueado${(slotsCount ?? 0) > 1 ? 's' : ''}`
              )}
            </Alert>
          )}

          {/* Error message */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleBlock}
          disabled={loading || slotsCount === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <Block />}
        >
          {loading ? 'Bloqueando...' : 'Bloquear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlockScheduleModal;
